import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  RevisionMetadata,
  SavedIndicationGraph,
} from 'features/reporting/types'
import {
  CalibrationPageData,
  calibrationPageDataSchema,
  ClientProvidedImagesData,
  clientProvidedImagesDataSchema,
  CoverPageData,
  coverPageDataSchema,
  MainInfo,
  mainInfoSchema,
  RautFormDataState,
  RautInspectionAssetDrawingsData,
  SummaryPageData,
  summaryPageDataSchema,
  WeldScanData,
  weldScanDataSchema,
} from 'pages/reporting/reports/raut/rautReportData'
import { getIndicationKey } from 'pages/reporting/reports/raut/utils/indicationUtils'
import { convertDatesToDay } from 'pages/reporting/shared/utils/dateUtils'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoReportingMetadata,
  LockState,
  SignatureData,
  SignoffState,
} from 'types'

export function deserializeGeckoMeasurementsAndDates<T>(data: T): T {
  const isGeckoMeasurementJson = (
    obj: unknown
  ): obj is GeckoMeasurementJson => {
    if (obj === null || typeof obj !== 'object') return false

    // Check if the object has 'num' and 'unit' properties
    if (!('num' in obj) || !('unit' in obj)) return false

    // Get the object with the proper type to access properties
    const candidate = obj as Record<string, unknown>

    // Check if num is a number and unit is a string
    return (
      typeof candidate.num === 'number' && typeof candidate.unit === 'string'
    )
  }

  if (data === null || data === undefined) return data

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) =>
      deserializeGeckoMeasurementsAndDates(item)
    ) as unknown as T
  }

  if (typeof data === 'string') {
    // Dayjs can't validate the UTC indicator Z,
    // so check for it, then remove it and try ISO8601 validating
    if (data.includes('Z')) {
      if (
        dayjs(
          data.slice(0, data.length - 1),
          'YYYY-MM-DDTHH:mm:ss.SSS',
          true
        ).isValid()
      ) {
        return dayjs(data) as unknown as T
      }
    }
  }

  // Handle objects
  if (typeof data === 'object') {
    // Check if this looks like a serialized GeckoMeasurement
    if (isGeckoMeasurementJson(data)) {
      // Recreate a proper GeckoMeasurement object
      return GeckoMeasurement.CreateFromJSON({
        num: data.num,
        unit: data.unit,
      }) as unknown as T
    }

    // Process each property recursively
    const result: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = deserializeGeckoMeasurementsAndDates(
          (data as Record<string, unknown>)[key]
        )
      }
    }
    return result as unknown as T
  }

  return data
}

export function getSelectedFormData(
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[]
) {
  const currentEntry = metadataEntries.find(
    (entry) => entry.id === metadataEntryId
  )

  if (!currentEntry) {
    console.warn('Current metadata entry not found')
    return
  }

  const existingData = currentEntry.data as Record<string, unknown>

  // Deserialize the data, properly reconstructing GeckoMeasurement objects
  const deserializedEntry = deserializeGeckoMeasurementsAndDates(existingData)
  const deserializedData = deserializedEntry['section_info'] as Record<
    string,
    unknown
  >

  const newFormData = {
    mainInfo: (deserializedData.main_info as MainInfo) || ({} as MainInfo),
    coverPageData: (() => {
      const raw = convertDatesToDay(
        (deserializedData.cover_page || {}) as Record<string, unknown>
      ) as Record<string, unknown>
      return {
        geckoJobId: raw.geckoJobId,
        startDate: raw.startDate,
        endDate: raw.endDate,
      } as CoverPageData
    })(),
    summaryPageData:
      (deserializedData.summary_page as SummaryPageData) ||
      ({} as SummaryPageData),
    calibrationData: convertDatesToDay(
      (deserializedData.calibration_page || {}) as Record<string, unknown>
    ) as unknown as CalibrationPageData,
    clientProvidedImagesData:
      (deserializedData.client_provided_images as ClientProvidedImagesData) ||
      ({} as ClientProvidedImagesData),
    asset_drawings:
      (deserializedData.asset_drawings as RautFormDataState['asset_drawings']) ||
      {},
    savedIndicationGraphs:
      (deserializedData.savedIndicationGraphs as Record<
        string,
        SavedIndicationGraph
      >) || ({} as Record<string, SavedIndicationGraph>),
    savedWeldIndicationGraphs:
      (deserializedData.savedWeldIndicationGraphs as RautFormDataState['savedWeldIndicationGraphs']) ||
      undefined,
    weldScanData: (() => {
      return (
        (deserializedEntry.weld_scan_data as RautFormDataState['weldScanData']) ||
        undefined
      )
    })(),
    signoff: (deserializedEntry.signoff as SignoffState) || {
      lastRevision: 0,
    },
    revisionMetadata:
      (deserializedEntry.revisionMetadata as RevisionMetadata) || {
        revisionNumber: 1,
        createdAt: new Date().toISOString(),
        isPublished: false,
      },
    lockState: (deserializedEntry.lockState as LockState) || {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: (deserializedEntry.publishedPdfPath as string) || '',
  }

  return newFormData
}

export const useFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean,
  refreshMetadataEntry: () => Promise<void>
) => {
  const [formData, setFormData] = useState<RautFormDataState>({
    mainInfo: {} as MainInfo,
    coverPageData: {} as CoverPageData,
    summaryPageData: {} as SummaryPageData,
    calibrationData: {} as CalibrationPageData,
    clientProvidedImagesData: {} as ClientProvidedImagesData,
    asset_drawings: {},
    weldScanData: undefined,
    signoff: {
      lastRevision: 0,
    },
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: '',
  })

  const { rolodexPatch } = useRolodexEdit()

  const lastSyncedEntryIdRef = useRef<string | null>(null)
  const lastSyncedEntryDataRef = useRef<string | null>(null)
  const formDataRef = useRef(formData)

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])

  const patchSection = useCallback(
    async (section: string, sectionData: Record<string, unknown>) => {
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            section_info: {
              [section]: sectionData,
            },
          },
        },
      ])
      refreshMetadataEntry().catch(console.error)
    },
    [metadataEntryId, rolodexPatch, refreshMetadataEntry]
  )

  useEffect(() => {
    if (isLoading) return

    const currentEntry = metadataEntries.find(
      (entry) => entry.id === metadataEntryId
    )
    if (!currentEntry) return

    const currentEntryData = JSON.stringify(currentEntry.data ?? {})
    if (
      lastSyncedEntryIdRef.current === metadataEntryId &&
      lastSyncedEntryDataRef.current === currentEntryData
    ) {
      return
    }

    const newFormData = getSelectedFormData(metadataEntryId, metadataEntries)
    if (newFormData) {
      setFormData(newFormData)
      lastSyncedEntryIdRef.current = metadataEntryId
      lastSyncedEntryDataRef.current = currentEntryData
    }
  }, [isLoading, metadataEntries, metadataEntryId])

  const updateFormData = useCallback((newData: Partial<RautFormDataState>) => {
    setFormData((prev) => ({ ...prev, ...newData }))
  }, [])

  const handleMainInfoFinish = useCallback(
    async (value: MainInfo) => {
      try {
        const merged = { ...formData.mainInfo, ...value }
        const parsed = mainInfoSchema.parse(merged)
        updateFormData({ mainInfo: parsed })

        await patchSection('main_info', { ...parsed })
        message.success('Main Info saved successfully!')
      } catch (error) {
        console.error('Failed to save main info:', error)
        message.error('Failed to save main info')
      }
    },
    [formData.mainInfo, patchSection, updateFormData]
  )

  const saveCoverPageSilent = useCallback(
    async (values: CoverPageData) => {
      const parsed = coverPageDataSchema.parse(values)

      const storageValues = {
        ...parsed,
        startDate: parsed.startDate?.toISOString(),
        endDate: parsed.endDate?.toISOString(),
      }

      updateFormData({
        coverPageData: parsed,
      })

      await patchSection('cover_page', { ...storageValues })
    },
    [updateFormData, patchSection]
  )

  const handleCoverPageFinish = useCallback(
    async (values: CoverPageData) => {
      try {
        await saveCoverPageSilent(values)

        message.success('Cover page saved successfully!')
      } catch (error) {
        console.error('Failed to save cover page:', error)
        message.error('Failed to save cover page')
      }
    },
    [saveCoverPageSilent]
  )

  const saveSummaryPageSilent = useCallback(
    async (values: SummaryPageData) => {
      const parsed = summaryPageDataSchema.parse(values)

      updateFormData({
        summaryPageData: parsed,
      })

      await patchSection('summary_page', { ...parsed })
    },
    [updateFormData, patchSection]
  )

  const saveMainInfoSilent = useCallback(
    async (value: Partial<MainInfo>) => {
      // Merge with existing mainInfo, providing default for required scanIndicationMode
      const merged = {
        ...formData.mainInfo,
        ...value,
        // Ensure scanIndicationMode has a value (required by schema)
        scanIndicationMode:
          value.scanIndicationMode ??
          formData.mainInfo.scanIndicationMode ??
          false,
      }
      const parsed = mainInfoSchema.parse(merged)
      updateFormData({ mainInfo: parsed })

      await patchSection('main_info', { ...parsed })
    },
    [formData.mainInfo, patchSection, updateFormData]
  )

  const handleSummaryPageFinish = useCallback(
    async (values: SummaryPageData) => {
      try {
        await saveSummaryPageSilent(values)
        message.success('Summary page saved successfully!')
      } catch (error) {
        console.error('Error saving summary page:', error)
        message.error('Failed to save summary page')
      }
    },
    [saveSummaryPageSilent]
  )

  const saveCalibrationSilent = useCallback(
    async (
      values: Partial<CalibrationPageData>,
      isComplete: boolean,
      deleteData: string[] = []
    ) => {
      const parsed: Partial<CalibrationPageData> = isComplete
        ? calibrationPageDataSchema.parse(values)
        : values

      const storageValues = {
        ...parsed,
        calibrationVerification: parsed.calibrationVerification?.map(
          (verification) => ({
            ...verification,
            date: verification.date?.toISOString() || null,
          })
        ),
        instrumentCalibrationDate:
          parsed.instrumentCalibrationDate?.toISOString(),
      }

      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            section_info: {
              calibration_page: storageValues,
            },
          },
          delete_data: deleteData.length > 0 ? deleteData : undefined,
        },
      ])
      refreshMetadataEntry().catch(console.error)
      updateFormData({ calibrationData: parsed as CalibrationPageData })
    },
    [metadataEntryId, refreshMetadataEntry, rolodexPatch, updateFormData]
  )

  const handleCalibrationFinish = useCallback(
    async (values: Partial<CalibrationPageData>, isComplete: boolean) => {
      try {
        await saveCalibrationSilent(values, isComplete)
        message.success('Calibration page saved successfully')
      } catch (error) {
        console.error('Failed to save calibration page:', error)
        message.error('Failed to save calibration page')
        throw error
      }
    },
    [saveCalibrationSilent]
  )

  const saveClientProvidedImagesSilent = useCallback(
    async (values: ClientProvidedImagesData) => {
      const parsed = clientProvidedImagesDataSchema.parse({
        images: values.images ?? [],
      })

      updateFormData({ clientProvidedImagesData: parsed })

      await patchSection('client_provided_images', parsed)
    },
    [updateFormData, patchSection]
  )

  const handleClientProvidedImagesFinish = useCallback(
    async (values: ClientProvidedImagesData) => {
      try {
        await saveClientProvidedImagesSilent(values)
        message.success('Client provided images saved successfully!')
      } catch (error) {
        console.error('Failed to save client provided images:', error)
        message.error('Failed to save client provided images')
      }
    },
    [saveClientProvidedImagesSilent]
  )
  const handleWeldScanDataFinish = useCallback(
    async (values: WeldScanData) => {
      try {
        const parsed = weldScanDataSchema.parse(values)

        updateFormData({ weldScanData: parsed })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              weld_scan_data: parsed,
            },
          },
        ])
        message.success('Weld scan data saved successfully!')
      } catch (error) {
        console.error('Failed to save weld scan data:', error)
        message.error('Failed to save weld scan data')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleAssetDrawingsFinish = useCallback(
    async (
      inspectionId: string,
      inspectionDrawings: RautInspectionAssetDrawingsData
    ) => {
      try {
        const updatedAssetDrawings = {
          ...formDataRef.current.asset_drawings,
          [inspectionId]: inspectionDrawings,
        }

        updateFormData({
          asset_drawings: updatedAssetDrawings,
        })

        await patchSection('asset_drawings', updatedAssetDrawings)
        message.success('Asset drawings saved successfully!')
      } catch {
        message.error('Failed to save asset drawings')
      }
    },
    [updateFormData, patchSection]
  )

  const handleSaveIndicationGraph = useCallback(
    async (graphData: SavedIndicationGraph) => {
      try {
        const updatedGraphs = {
          ...(formData.savedIndicationGraphs || {}),
          [graphData.indicationId]: graphData,
        }

        updateFormData({ savedIndicationGraphs: updatedGraphs })

        await patchSection('savedIndicationGraphs', updatedGraphs)

        return updatedGraphs
      } catch (error) {
        console.error('Failed to save indication graph:', error)
        message.error('Failed to save indication graph')
        throw error
      }
    },
    [formData.savedIndicationGraphs, patchSection, updateFormData]
  )

  const handleDeleteIndicationGraph = useCallback(
    async (indicationId: string) => {
      try {
        const updatedGraphs = { ...(formData.savedIndicationGraphs || {}) }
        delete updatedGraphs[indicationId]

        updateFormData({ savedIndicationGraphs: updatedGraphs })

        await rolodexPatch([
          {
            id: metadataEntryId,
            delete_data: [`section_info.savedIndicationGraphs.${indicationId}`],
          },
        ])

        return updatedGraphs
      } catch (error) {
        console.error('Failed to delete indication graph:', error)
        message.error('Failed to delete indication graph')
        throw error
      }
    },
    [
      formData.savedIndicationGraphs,
      metadataEntryId,
      rolodexPatch,
      updateFormData,
    ]
  )

  const handleSaveWeldIndicationGraph = useCallback(
    async (indicationId: string, imagePath: string) => {
      try {
        const key = getIndicationKey(indicationId)
        const updatedGraphs = {
          ...(formData.savedWeldIndicationGraphs || {}),
          [key]: {
            imagePath,
            timestamp: new Date().toISOString(),
          },
        }

        updateFormData({ savedWeldIndicationGraphs: updatedGraphs })

        await patchSection('savedWeldIndicationGraphs', updatedGraphs)

        return updatedGraphs
      } catch (error) {
        console.error('Failed to save weld indication graph:', error)
        message.error('Failed to save weld indication graph')
        throw error
      }
    },
    [formData.savedWeldIndicationGraphs, patchSection, updateFormData]
  )

  const handleDeleteWeldIndicationGraph = useCallback(
    async (indicationId: string) => {
      try {
        const key = getIndicationKey(indicationId)
        const updatedGraphs = {
          ...(formData.savedWeldIndicationGraphs || {}),
        }
        delete updatedGraphs[key]

        updateFormData({ savedWeldIndicationGraphs: updatedGraphs })

        await rolodexPatch([
          {
            id: metadataEntryId,
            delete_data: [`section_info.savedWeldIndicationGraphs.${key}`],
          },
        ])

        return updatedGraphs
      } catch (error) {
        console.error('Failed to delete weld indication graph:', error)
        message.error('Failed to delete weld indication graph')
        throw error
      }
    },
    [
      formData.savedWeldIndicationGraphs,
      metadataEntryId,
      rolodexPatch,
      updateFormData,
    ]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const updatedSignoff: SignoffState = {
          ...(formData.signoff || { lastRevision: 0 }),
          [signatureData.role === 'supervisor'
            ? 'supervisorSignoff'
            : 'reviewerSignoff']: signatureData,
        }

        // Increment revision if reviewer is signing
        if (
          signatureData.role === 'reviewer' &&
          updatedSignoff.supervisorSignoff
        ) {
          updatedSignoff.lastRevision = (updatedSignoff.lastRevision || 0) + 1
        }

        // Update Rolodex first
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        // Then update local state
        updateFormData({
          signoff: updatedSignoff,
        })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [formData.signoff, metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverPageFinish,
    saveCoverPageSilent,
    handleSummaryPageFinish,
    saveSummaryPageSilent,
    saveMainInfoSilent,
    saveCalibrationSilent,
    handleCalibrationFinish,
    handleClientProvidedImagesFinish,
    saveClientProvidedImagesSilent,
    handleWeldScanDataFinish,
    handleAssetDrawingsFinish,
    handleSaveIndicationGraph,
    handleDeleteIndicationGraph,
    handleSaveWeldIndicationGraph,
    handleDeleteWeldIndicationGraph,
    handleSignoffFinish,
  }
}
